import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ProjectPanel from './components/ProjectPanel';
import TimerWidget from './components/TimerWidget';
import TimesheetGrid from './components/TimesheetGrid';
import WeeklySummary from './components/WeeklySummary';
import ProjectFormModal from './components/ProjectFormModal';
import Button from '../../components/ui/Button';


const TimeTracking = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday?.setDate(today?.getDate() - today?.getDay() + 1);
    return monday?.toISOString()?.split('T')?.[0];
  });
  const [showTimer, setShowTimer] = useState(true);
  const [activeView, setActiveView] = useState('timesheet'); // 'timesheet' or 'summary'
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  // Clean state without dummy data
  const [projects, setProjects] = useState([]);

  // Clean state without dummy data
  const [timeEntries, setTimeEntries] = useState([]);

  useEffect(() => {
    if (projects?.length > 0 && !selectedProject) {
      setSelectedProject(projects?.find(p => p?.status === 'active') || projects?.[0]);
    }
  }, [projects, selectedProject]);

  const handleProjectSelect = (project) => {
    console.log('Selecting project:', project?.name);
    setSelectedProject(project);
  };

  const handleNewProject = () => {
    console.log('Opening new project modal...');
    setEditingProject(null);
    setShowProjectModal(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowProjectModal(true);
  };

  const handleProjectSubmit = async (projectData) => {
    try {
      if (editingProject) {
        // Update existing project
        setProjects(prev => prev?.map(project => 
          project?.id === editingProject?.id ? projectData : project
        ));
        console.log('Project updated:', projectData?.name);
      } else {
        // Add new project
        setProjects(prev => [...prev, projectData]);
        console.log('Project created:', projectData?.name);
        
        // Auto-select the new project if no project is currently selected
        if (!selectedProject) {
          setSelectedProject(projectData);
        }
      }
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const handleTimeEntry = (entry) => {
    const newEntry = {
      ...entry,
      id: `entry-${Date.now()}`,
      billable: true
    };
    setTimeEntries(prev => [...prev, newEntry]);
    console.log('Time entry added:', newEntry);
  };

  const handleUpdateEntry = (updatedEntry) => {
    setTimeEntries(prev => prev?.map(entry => 
      entry?.id === updatedEntry?.id ? updatedEntry : entry
    ));
  };

  const handleDeleteEntry = (entryId) => {
    setTimeEntries(prev => prev?.filter(entry => entry?.id !== entryId));
  };

  const handleAddEntry = (newEntry) => {
    setTimeEntries(prev => [...prev, newEntry]);
  };

  // Add export functionality
  const handleExport = () => {
    try {
      let csvData = [];
      let filename = '';

      if (activeView === 'timesheet') {
        // Export daily timesheet data
        const dailyEntries = timeEntries?.filter(entry => entry?.date === selectedDate);
        filename = `timesheet-${selectedDate}.csv`;
        
        csvData = [
          ['Date', 'Project', 'Client', 'Task', 'Start Time', 'End Time', 'Duration (hours)', 'Billable', 'Rate', 'Revenue']
        ];

        dailyEntries?.forEach(entry => {
          const project = projects?.find(p => p?.id === entry?.projectId);
          const revenue = entry?.billable ? (entry?.duration * (project?.hourlyRate || 0)) : 0;
          
          csvData?.push([
            entry?.date,
            project?.name || 'Unknown Project',
            project?.client || 'Unknown Client',
            entry?.task,
            entry?.startTime,
            entry?.endTime,
            entry?.duration?.toFixed(2),
            entry?.billable ? 'Yes' : 'No',
            project?.hourlyRate ? `$${project?.hourlyRate}` : '$0',
            `$${revenue?.toFixed(2)}`
          ]);
        });
      } else {
        // Export weekly summary data
        const weekDates = [];
        const start = new Date(selectedWeek);
        for (let i = 0; i < 7; i++) {
          const date = new Date(start);
          date?.setDate(start?.getDate() + i);
          weekDates?.push(date?.toISOString()?.split('T')?.[0]);
        }
        
        const weekEntries = timeEntries?.filter(entry => weekDates?.includes(entry?.date));
        filename = `weekly-summary-${selectedWeek}.csv`;
        
        csvData = [
          ['Date', 'Project', 'Client', 'Task', 'Duration (hours)', 'Billable', 'Revenue']
        ];

        weekEntries?.forEach(entry => {
          const project = projects?.find(p => p?.id === entry?.projectId);
          const revenue = entry?.billable ? (entry?.duration * (project?.hourlyRate || 0)) : 0;
          
          csvData?.push([
            entry?.date,
            project?.name || 'Unknown Project',
            project?.client || 'Unknown Client',
            entry?.task,
            entry?.duration?.toFixed(2),
            entry?.billable ? 'Yes' : 'No',
            `$${revenue?.toFixed(2)}`
          ]);
        });

        // Add summary totals
        const totalHours = weekEntries?.reduce((sum, entry) => sum + entry?.duration, 0);
        const totalBillableHours = weekEntries?.filter(entry => entry?.billable)?.reduce((sum, entry) => sum + entry?.duration, 0);
        const totalRevenue = weekEntries?.filter(entry => entry?.billable)?.reduce((sum, entry) => {
          const project = projects?.find(p => p?.id === entry?.projectId);
          return sum + (entry?.duration * (project?.hourlyRate || 0));
        }, 0);

        csvData?.push([]);
        csvData?.push(['SUMMARY', '', '', '', '', '', '']);
        csvData?.push(['Total Hours', '', '', '', totalHours?.toFixed(2), '', '']);
        csvData?.push(['Billable Hours', '', '', '', totalBillableHours?.toFixed(2), '', '']);
        csvData?.push(['Total Revenue', '', '', '', '', '', `$${totalRevenue?.toFixed(2)}`]);
      }

      // Convert array to CSV string
      const csvContent = csvData?.map(row => 
        row?.map(field => 
          typeof field === 'string' && field?.includes(',') 
            ? `"${field}"` 
            : field
        )?.join(',')
      )?.join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document?.createElement('a');
      
      if (link?.download !== undefined) {
        const url = URL?.createObjectURL(blob);
        link?.setAttribute('href', url);
        link?.setAttribute('download', filename);
        link?.style && (link.style.visibility = 'hidden');
        document?.body?.appendChild(link);
        link?.click();
        document?.body?.removeChild(link);
      }

      console?.log(`Exported ${csvData?.length - 1} entries to ${filename}`);
    } catch (error) {
      console?.error('Export failed:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex pt-16">
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        
        <div className="flex-1 flex">
          {/* Project Panel */}
          <ProjectPanel
            projects={projects}
            selectedProject={selectedProject}
            onProjectSelect={handleProjectSelect}
            onNewProject={handleNewProject}
            timeEntries={timeEntries}
          />

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {/* View Toggle */}
            <div className="p-6 border-b border-border bg-surface">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
                    <Button
                      variant={activeView === 'timesheet' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setActiveView('timesheet')}
                    >
                      Timesheet
                    </Button>
                    <Button
                      variant={activeView === 'summary' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setActiveView('summary')}
                    >
                      Weekly Summary
                    </Button>
                  </div>
                  
                  {/* Decorative Image */}
                  <div className="hidden lg:flex items-center ml-8">
                    <img 
                      src="/assets/images/image-1761885647241.png" 
                      alt="Professional time tracking interface showcasing productivity tools and project management features"
                      className="w-12 h-12 object-contain opacity-30"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Button
                    variant={showTimer ? 'default' : 'outline'}
                    size="sm"
                    iconName={showTimer ? 'EyeOff' : 'Eye'}
                    iconPosition="left"
                    onClick={() => setShowTimer(!showTimer)}
                  >
                    {showTimer ? 'Hide Timer' : 'Show Timer'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden">
              {activeView === 'timesheet' ? (
                <TimesheetGrid
                  timeEntries={timeEntries}
                  projects={projects}
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  onUpdateEntry={handleUpdateEntry}
                  onDeleteEntry={handleDeleteEntry}
                  onAddEntry={handleAddEntry}
                />
              ) : (
                <div className="p-6 overflow-auto">
                  <WeeklySummary
                    timeEntries={timeEntries}
                    projects={projects}
                    selectedWeek={selectedWeek}
                    onWeekChange={setSelectedWeek}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Floating Timer Widget */}
        {showTimer && (
          <TimerWidget
            projects={projects?.filter(p => p?.status === 'active')}
            onTimeEntry={handleTimeEntry}
            isFloating={true}
          />
        )}
      </div>

      {/* Project Form Modal */}
      <ProjectFormModal
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        onSubmit={handleProjectSubmit}
        initialData={editingProject}
      />
    </div>
  );
};

export default TimeTracking;